/**
 * Wrap an axios call for createAsyncThunk so a failed request rejects with the
 * API's response body (`resp.payload`), not just axios' "Request failed with
 * status code 500". Callers can then show the server's reason, e.g. via
 * `errorMessage({ data: resp.payload })`.
 */
const GATEWAY_GAVE_UP = [502, 503, 504]

/**
 * True when a failed request got no answer from the service itself: a
 * network error or timeout, or the ingress giving up with 502/503/504. The
 * service may still be working on it (a write may have gone through, a
 * report may still be generated). A 502/503/504 carrying the API's own error
 * body (e.g. "Could not check whether the template name is taken") is an
 * answer, not a timeout.
 *
 * @param {object} err  an axios error
 * @returns {boolean}
 */
export const isNoAnswer = (err) => {
    if (!err?.response) return true
    const data = err.response.data
    const apiAnswered =
        data && typeof data === 'object' && (data.message || data.error)
    return GATEWAY_GAVE_UP.includes(err.response.status) && !apiAnswered
}

const withApiError = (fn) => async (arg, thunkApi) => {
    try {
        return await fn(arg)
    } catch (err) {
        // No answer from the service: say so, so a caller can find out
        // whether a write went through anyway.
        if (isNoAnswer(err))
            return thunkApi.rejectWithValue({
                error: 'The server did not answer in time.',
                noResponse: true,
                status: err?.response?.status,
            })
        return thunkApi.rejectWithValue(
            err.response.data ?? { error: err.message }
        )
    }
}

export default withApiError
